import { useEffect, useRef, useState, RefObject } from 'react';

export interface UseIntersectionObserverOptions {
  /**
   * Threshold(s) at which to trigger the callback
   * Can be a number (0-1) or array of numbers
   * @default 0
   */
  threshold?: number | number[];
  /**
   * Root margin for the intersection observer
   * @default '0px'
   */
  rootMargin?: string;
  /**
   * Whether to disconnect the observer after first intersection
   * Useful for lazy loading scenarios
   * @default false
   */
  disconnectOnIntersect?: boolean;
  /**
   * Whether to trigger only when fully visible (threshold: 1.0)
   * This is a convenience option that overrides threshold
   * @default false
   */
  fullyVisible?: boolean;
  /**
   * Whether the hook is enabled
   * @default true
   */
  enabled?: boolean;
}

export interface UseIntersectionObserverResult<T extends HTMLElement = HTMLElement> {
  /**
   * Ref to attach to the element you want to observe
   */
  ref: RefObject<T | null>;
  /**
   * Whether the element is currently intersecting
   */
  isIntersecting: boolean;
  /**
   * Intersection ratio (0-1)
   */
  intersectionRatio: number;
  /**
   * Whether the element is fully visible (intersectionRatio >= 1.0)
   */
  isFullyVisible: boolean;
  /**
   * Whether the element meets the configured threshold
   * This is based on the threshold option passed to the hook
   */
  meetsThreshold: boolean;
}

/**
 * Custom hook to observe when an element enters/exits the viewport
 * 
 * @param options - Configuration options for the IntersectionObserver
 * @param callback - Optional callback function called when intersection changes
 * @returns Object containing ref, isIntersecting, intersectionRatio, and isFullyVisible
 * 
 * @example
 * // Basic usage - detect when element is in viewport
 * const { ref, isIntersecting } = useIntersectionObserver();
 * 
 * @example
 * // Detect when fully visible
 * const { ref, isFullyVisible } = useIntersectionObserver({ fullyVisible: true });
 * 
 * @example
 * // With callback
 * const { ref } = useIntersectionObserver(
 *   { threshold: 0.5 },
 *   (entry) => console.log('Intersecting:', entry.isIntersecting)
 * );
 * 
 * @example
 * // Lazy loading pattern
 * const { ref, isIntersecting } = useIntersectionObserver({
 *   rootMargin: '50px',
 *   disconnectOnIntersect: true
 * });
 */
export function useIntersectionObserver<T extends HTMLElement = HTMLElement>(
  options: UseIntersectionObserverOptions = {},
  callback?: (entry: IntersectionObserverEntry) => void
): UseIntersectionObserverResult<T> {
  const {
    threshold = 0,
    rootMargin = '0px',
    disconnectOnIntersect = false,
    fullyVisible = false,
    enabled = true,
  } = options;

  const ref = useRef<T>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [intersectionRatio, setIntersectionRatio] = useState(0);

  useEffect(() => {
    if (!enabled || !ref.current) return;

    const element = ref.current;

    // Determine threshold based on options
    const observerThreshold = fullyVisible ? 1.0 : threshold;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const intersecting = entry.isIntersecting;
          const ratio = entry.intersectionRatio;

          setIsIntersecting(intersecting);
          setIntersectionRatio(ratio);

          // Call optional callback
          if (callback) {
            callback(entry);
          }

          // Disconnect if configured to do so after first intersection
          if (disconnectOnIntersect && intersecting) {
            observer.disconnect();
          }
        });
      },
      {
        threshold: observerThreshold,
        rootMargin,
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [threshold, rootMargin, disconnectOnIntersect, fullyVisible, enabled, callback]);

  const isFullyVisible = intersectionRatio >= 1.0;
  
  // Determine the effective threshold to check against
  const effectiveThreshold = fullyVisible ? 1.0 : (Array.isArray(threshold) ? threshold[0] : threshold);
  const meetsThreshold = intersectionRatio >= effectiveThreshold;

  return {
    ref,
    isIntersecting,
    intersectionRatio,
    isFullyVisible,
    meetsThreshold,
  };
}

export interface UseFullyVisibleOptions {
  /**
   * Threshold at which to consider the element visible (0-1)
   * @default 1.0 (100% visible)
   * @example 0.8 for 80% visible
   */
  threshold?: number;
  /**
   * Root margin for the intersection observer
   * @default '0px'
   */
  rootMargin?: string;
  /**
   * Optional callback function called when visibility changes
   */
  callback?: (entry: IntersectionObserverEntry) => void;
}

/**
 * Convenience hook for detecting when an element is visible in viewport
 * By default detects when fully visible (100%), but threshold is configurable
 * 
 * @param options - Configuration options including threshold (default: 1.0)
 * @returns Object containing ref, isFullyVisible (100%), and meetsThreshold (based on configured threshold)
 * 
 * @example
 * // Default: 100% visible
 * const { ref, isFullyVisible, meetsThreshold } = useFullyVisible<HTMLDivElement>();
 * 
 * @example
 * // 80% visible
 * const { ref, meetsThreshold } = useFullyVisible<HTMLDivElement>({ threshold: 0.8 });
 * 
 * @example
 * // With callback
 * const { ref } = useFullyVisible<HTMLDivElement>({
 *   threshold: 0.8,
 *   callback: (entry) => console.log('80% visible:', entry.isIntersecting)
 * });
 */
export function useFullyVisible<T extends HTMLElement = HTMLElement>(
  options: UseFullyVisibleOptions = {}
): { 
  ref: RefObject<T | null>; 
  isFullyVisible: boolean;
  meetsThreshold: boolean;
} {
  const { threshold = 1.0, rootMargin, callback } = options;
  
  const { ref, isFullyVisible, meetsThreshold } = useIntersectionObserver<T>(
    { threshold, rootMargin, fullyVisible: threshold === 1.0 },
    callback
  );
  
  return { ref, isFullyVisible, meetsThreshold };
}

